/* B4 admin pages: WS1 workspace + RFI authoring + RFI review. */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { usePageMeta } from "../../components/Layout";
import { readErrorDetail } from "../../lib/errors";
import { ArrowLeft, Plus, Trash2, ChevronUp, ChevronDown, X } from "lucide-react";

const RESPONSE_TYPES = [
    { value: "single_file", label: "Single file" },
    { value: "multiple_files", label: "Multiple files" },
    { value: "images", label: "Images" },
    { value: "text", label: "Text" },
    { value: "reference", label: "Reference or identifier" },
    { value: "reference_or_file", label: "Reference OR file" },
    { value: "text_and_files", label: "Text + files" },
];
const FSI_OPTS = [
    ["free_design_only", "Free Design only / likely own team"],
    ["unsure", "Unsure at this stage"],
    ["further_design_technical", "Interested in further design/technical"],
    ["project_management", "Interested in project-management/contract-admin"],
    ["construction_design_build", "Interested in construction/design-and-build"],
    ["tailored_combination", "Open to a tailored combination"],
];
const OUTCOMES = [
    ["ready_for_feasibility", "Ready for feasibility review"],
    ["more_info_required", "More information required"],
    ["hold_recommended", "Hold recommended"],
    ["close_recommended", "Close recommended"],
];
// Read-path alias map (§22) — legacy codes on historic records must
// still render with the new human labels; new writes never emit these.
const OUTCOME_LABEL = {
    ready_for_feasibility: "Ready for feasibility review",
    more_info_required: "More information required",
    hold_recommended: "Hold recommended",
    close_recommended: "Close recommended",
    // Legacy aliases — kept for historic records only.
    place_on_hold: "Hold recommended",
    close_project: "Close recommended",
};
const BUDGET_CONF = ["firm", "approximate", "ceiling", "guidance_needed"];

/* ---------- WS1 modules (shared inline component) ---------- */
/*
 * D5 Errata 3a — shared inline module renderer used by BOTH the
 * dedicated `/admin/projects/:id/ws1` route (via `WS1Page` wrapper)
 * AND the Project Workspace accordion `num === 1` active branch
 * (via `ProjectWorkspace.jsx` inline consumption).  Mirrors the
 * `WS2ActiveModules` / `WS3ActiveModules` / `WS4ActiveModules`
 * pattern — `{ projectId, onCompleted }` prop shape, own state
 * fetch, own modal orchestration.  No shape change vs the previous
 * inline JSX; only the wrapping was extracted.
 */
export function WS1ActiveModules({ projectId, mode = "active", onCompleted }) {
    const id = projectId;
    const navigate = useNavigate();
    const [ws, setWs] = useState(null);
    const [showInitial, setShowInitial] = useState(false);
    const [showQual, setShowQual] = useState(false);
    const [msg, setMsg] = useState("");
    // Errata 4 — stable op-ids for the two inline authoritative POSTs.
    // Modals already carry their own opIds (`opId` useMemo below).
    const opIdReview = useMemo(
        () => `ws1-r-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, []);
    const opIdComplete = useMemo(
        () => `ws1-c-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, []);
    const load = async () => {
        try {
            const r = await api.get(`/admin/projects/${id}/ws1`);
            setWs(r.data);
        } catch (e) { setMsg(readErrorDetail(e, "Failed to load WS1")); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
    const markReviewed = async () => {
        try {
            await api.post(`/admin/projects/${id}/ws1/enquiry-reviewed`,
                { create_op_id: opIdReview });
            toast.success("Enquiry reviewed");
            load();
        } catch (e) { toast.error(readErrorDetail(e, "Failed")); }
    };
    const completeWs = async () => {
        try {
            await api.post(`/admin/projects/${id}/ws1/complete`,
                { create_op_id: opIdComplete });
            toast.success("WS1 completed");
            if (onCompleted) onCompleted();
            load();
        } catch (e) {
            // Preserve the blockers-first UX when the backend explicitly
            // returned a blockers array; otherwise fall through to the
            // shared readable extractor.
            const d = e?.response?.data?.detail;
            const blockers = d && Array.isArray(d.blockers) ? d.blockers : null;
            toast.error(
                blockers && blockers.length
                    ? blockers.join("; ")
                    : readErrorDetail(e, "Failed"),
            );
        }
    };
    if (msg) return <div className="p-4 text-terracotta text-[13px]" data-testid="ws1-error">{msg}</div>;
    if (!ws) return <div className="p-4 text-[#666] text-[13px]">Loading…</div>;
    const er = ws.enquiry_review || {};
    const ic = ws.initial_contact || {};
    const q = ws.qualification || {};
    const c = ws.completion || {};
    // D5 Slice B — coherent mode propagation via a single fieldset
    // gate.  HTML's `disabled` attribute cascades to every nested
    // button/input/select/textarea, preventing mutations when the
    // workstage is not Active without repeating the guard on each
    // control.  Backend `_require_ws1_active` remains authoritative
    // (a disabled control is UX only, not security).
    const readOnly = mode !== "active";
    const modeBanner = readOnly ? (
        <div className="mb-3 px-3 py-2 rounded-md bg-[#F5F1EA] border border-[#E5E2DC] text-[12px] text-[#666]"
             data-testid={`ws1-mode-banner-${mode}`}>
            {mode === "future"
                ? "This Workstage is not yet active. Content is shown for reference only."
                : mode === "completed"
                ? "This Workstage is completed. Historical data shown, read-only."
                : "This Workstage is currently locked. Content is shown for reference only."}
        </div>
    ) : null;
    return (
        <fieldset disabled={readOnly} className="border-0 p-0 m-0 min-w-0"
                  data-testid={`ws1-fieldset-mode-${mode}`}>
        {modeBanner}
        <div className="flex flex-col gap-4" data-testid="ws1-active-modules">
            {/* Module 1 · Enquiry Review */}
            <section className="bg-white border border-[#E5E2DC] rounded-lg p-5 flex flex-col gap-3" data-testid="ws1-module-enquiry-review">
                <div className="flex items-center justify-between">
                    <h2 className="font-manrope font-semibold text-[15px]">Enquiry Review</h2>
                    {er.reviewed_at
                        ? <span className="text-[12px] text-[#666]" data-testid="ws1-reviewed-at">Reviewed {er.reviewed_at}</span>
                        : <button onClick={markReviewed} data-testid="ws1-mark-reviewed"
                            className="text-[13px] px-3 py-1.5 bg-charcoal text-white rounded-md hover:bg-black">
                            Mark Enquiry Reviewed
                        </button>}
                </div>
                <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-[13px]">
                    <div><dt className="text-[11px] uppercase text-[#999]">Source</dt><dd>{er.source}</dd></div>
                    <div><dt className="text-[11px] uppercase text-[#999]">Received</dt><dd>{er.received_at}</dd></div>
                    <div><dt className="text-[11px] uppercase text-[#999]">Property</dt><dd>{er.property_status}</dd></div>
                    <div><dt className="text-[11px] uppercase text-[#999]">Project type</dt><dd>{(er.project_types || []).join(", ")}</dd></div>
                    <div><dt className="text-[11px] uppercase text-[#999]">Budget</dt><dd>{er.construction_budget}</dd></div>
                    <div><dt className="text-[11px] uppercase text-[#999]">Timing</dt><dd>{er.construction_timing}</dd></div>
                    <div><dt className="text-[11px] uppercase text-[#999]">Postcode</dt><dd>{er.postcode}</dd></div>
                </dl>
                <div className="text-[13px] whitespace-pre-wrap">{er.project_description}</div>
                <button onClick={() => navigate(`/admin/projects/${id}`)}
                    className="self-start text-[12px] text-[#666] underline"
                    data-testid="ws1-view-full-enquiry">
                    View Full Original Enquiry
                </button>
            </section>

            {/* Module 2 · Initial Contact */}
            <section className="bg-white border border-[#E5E2DC] rounded-lg p-5 flex flex-col gap-3" data-testid="ws1-module-initial-contact">
                <h2 className="font-manrope font-semibold text-[15px]">Initial Contact</h2>
                {!ic.sent_at ? (
                    <>
                        <div className="text-[13px]">
                            SLA deadline: <span data-testid="ws1-sla-deadline">{ic.sla_deadline_at}</span>
                        </div>
                        <div className="text-[13px]" data-testid="ws1-send-state">Not yet sent</div>
                        <div className="flex flex-wrap items-center gap-3">
                            <button onClick={() => setShowInitial(true)}
                                data-testid="ws1-open-initial-response"
                                className="text-[13px] px-3 py-1.5 bg-charcoal text-white rounded-md hover:bg-black">
                                Send initial response
                            </button>
                            <ContactMadeControl projectId={id} ic={ic} onChanged={load} />
                        </div>
                    </>
                ) : (
                    <>
                        <div className="text-[13px]">Sent <span data-testid="ws1-sent-at">{ic.sent_at}</span></div>
                        {ic.communication_id && (
                            <button className="self-start text-[12px] underline"
                                onClick={() => navigate(`/admin/projects/${id}/communications`)}
                                data-testid="ws1-view-email">View Email</button>
                        )}
                        <ContactMadeControl projectId={id} ic={ic} onChanged={load} />
                    </>
                )}
            </section>

            {/* Module 3 · Qualification */}
            <section className="bg-white border border-[#E5E2DC] rounded-lg p-5 flex flex-col gap-3" data-testid="ws1-module-qualification">
                <div className="flex items-center justify-between">
                    <h2 className="font-manrope font-semibold text-[15px]">Qualification</h2>
                    {!q.completed_at
                        ? <button onClick={() => setShowQual(true)} data-testid="ws1-open-qualification"
                            className="text-[13px] px-3 py-1.5 bg-charcoal text-white rounded-md hover:bg-black">
                            Start Qualification
                        </button>
                        : <span className="text-[12px] text-[#666]">Completed {q.completed_at}</span>}
                </div>
                {q.completed_at && (
                    <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-[13px]">
                        <div><dt className="text-[11px] uppercase text-[#999]">Lead Quality</dt><dd data-testid="ws1-lead-quality">{q.lead_quality}</dd></div>
                        <div><dt className="text-[11px] uppercase text-[#999]">Outcome</dt><dd data-testid="ws1-qualification-outcome">{OUTCOME_LABEL[q.outcome] || q.outcome}</dd></div>
                        <div><dt className="text-[11px] uppercase text-[#999]">FSI</dt><dd>{q.future_service_intention}</dd></div>
                        <div><dt className="text-[11px] uppercase text-[#999]">Budget confidence</dt><dd>{q.budget_confidence}</dd></div>
                    </dl>
                )}
            </section>

            {/* Module 4 · Workstage Completion */}
            <section className="bg-white border border-[#E5E2DC] rounded-lg p-5 flex flex-col gap-3" data-testid="ws1-module-completion">
                <h2 className="font-manrope font-semibold text-[15px]">Workstage Completion</h2>
                {c.completed_at ? (
                    <div className="text-[13px]" data-testid="ws1-completion-summary">
                        Enquiry &amp; Qualification — completed {c.completed_at} · outcome {c.outcome}
                    </div>
                ) : (
                    <>
                        {(c.blockers || []).length > 0 && (
                            <ul className="text-[12px] text-terracotta list-disc pl-5" data-testid="ws1-blockers">
                                {c.blockers.map((b, i) => <li key={i}>{b}</li>)}
                            </ul>
                        )}
                        <button onClick={completeWs} disabled={(c.blockers || []).length > 0}
                            data-testid="ws1-complete"
                            className="self-start text-[13px] px-3 py-1.5 bg-charcoal text-white rounded-md hover:bg-black disabled:opacity-40">
                            Complete Workstage
                        </button>
                    </>
                )}
            </section>

            {showInitial && <InitialResponseModal projectId={id} onClose={() => { setShowInitial(false); load(); }} />}
            {showQual && <QualificationModal projectId={id} onClose={() => { setShowQual(false); load(); }} />}
        </div>
        </fieldset>
    );
}


/* ---------- WS1 dedicated route wrapper ----------
 * D5 Errata 3a — thin wrapper around the shared `WS1ActiveModules`
 * component.  The dedicated `/admin/projects/:id/ws1` route is
 * retained so existing deep links keep working; it now shows the
 * back button + heading + the same shared module renderer used by
 * the Project Workspace accordion.  No behavioural duplication.
 */
export function WS1Page() {
    const { id } = useParams();
    const navigate = useNavigate();
    usePageMeta({ title: "WS1 Enquiry · Project | Studio G4 Admin" });
    return (
        <div className="max-w-5xl mx-auto p-6 flex flex-col gap-4" data-testid="ws1-page">
            <button onClick={() => navigate(`/admin/projects/${id}`)}
                className="self-start text-[13px] flex items-center gap-1 text-[#666] hover:text-charcoal"
                data-testid="ws1-back">
                <ArrowLeft className="w-4 h-4" /> Back to workspace
            </button>
            <h1 className="text-2xl font-serif text-charcoal">Workstage 1 · Enquiry &amp; Qualification</h1>
            <WS1ActiveModules projectId={id} />
        </div>
    );
}


/* ---------- Contact Made control (§22 off-platform unblock) ---------- */
const CONTACT_ROUTE_LABEL = {
    phone_call: "Phone call",
    email: "Email",
    whatsapp: "WhatsApp",
};

function ContactMadeControl({ projectId, ic, onChanged }) {
    const cm = ic?.contact_made || null;
    const platformSent = !!ic?.initial_response_sent_at;
    const [ticked, setTicked] = useState(!!cm);
    const [route, setRoute] = useState(cm?.route || "phone_call");
    const [contactTs, setContactTs] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    useEffect(() => {
        setTicked(!!cm);
        setRoute(cm?.route || "phone_call");
    }, [cm?.route, cm?.contact_ts, cm]);
    const onToggle = async (checked) => {
        setErr("");
        if (checked) {
            setTicked(true);
            return;
        }
        // Un-tick: if a record exists, confirm delete.
        if (cm) {
            if (!window.confirm("Remove contact-made record?")) return;
            setBusy(true);
            try {
                await api.delete(
                    `/admin/projects/${projectId}/ws1/contact-made`);
                setTicked(false);
                await onChanged();
            } catch (e) { setErr(readErrorDetail(e, "Failed to remove")); }
            finally { setBusy(false); }
        } else {
            setTicked(false);
        }
    };
    const save = async () => {
        setBusy(true); setErr("");
        try {
            await api.post(
                `/admin/projects/${projectId}/ws1/contact-made`,
                { route, contact_ts: contactTs ? new Date(contactTs).toISOString() : null });
            setContactTs("");
            await onChanged();
        } catch (e) { setErr(readErrorDetail(e, "Failed to save")); }
        finally { setBusy(false); }
    };
    return (
        <div className="flex flex-col gap-2" data-testid="ws1-contact-made-control">
            <label className="inline-flex items-center gap-2 text-[13px]">
                <input type="checkbox" checked={ticked}
                        onChange={(e) => onToggle(e.target.checked)}
                        disabled={busy}
                        data-testid="ws1-contact-made-toggle" />
                Contact made
                {platformSent && (
                    <span className="text-[11px] text-[#888] italic"
                          data-testid="ws1-contact-made-platform-hint">
                        Initial response already recorded via platform email
                    </span>
                )}
            </label>
            {ticked && cm && (
                <div className="text-[12px] text-[#333] bg-[#F5F1EA] border border-[#E5E2DC] rounded px-2 py-1 inline-flex items-center gap-2"
                      data-testid="ws1-contact-made-summary">
                    <span>Contact made · {CONTACT_ROUTE_LABEL[cm.route] || cm.route}
                        {cm.contact_ts ? ` · ${cm.contact_ts}` : ""}
                    </span>
                </div>
            )}
            {ticked && !cm && (
                <div className="flex flex-col gap-2 border border-[#E5E2DC] rounded p-3 bg-[#FAFAF7]"
                      data-testid="ws1-contact-made-form">
                    <label className="text-[12px]">Contact route
                        <select value={route}
                                onChange={(e) => setRoute(e.target.value)}
                                className="mt-1 w-full border border-[#E5E2DC] rounded px-2 py-1 text-[13px]"
                                data-testid="ws1-contact-made-route">
                            <option value="phone_call">Phone call</option>
                            <option value="email">Email</option>
                            <option value="whatsapp">WhatsApp</option>
                        </select>
                    </label>
                    <label className="text-[12px]">Contact date/time (optional)
                        <input type="datetime-local" value={contactTs}
                                onChange={(e) => setContactTs(e.target.value)}
                                className="mt-1 w-full border border-[#E5E2DC] rounded px-2 py-1 text-[13px]"
                                data-testid="ws1-contact-made-ts" />
                    </label>
                    {err && <div className="text-[12px] text-terracotta"
                                   data-testid="ws1-contact-made-err">{err}</div>}
                    <button type="button" onClick={save} disabled={busy}
                             className="self-start text-[12px] px-3 py-1 bg-charcoal text-white rounded hover:bg-black disabled:opacity-60"
                             data-testid="ws1-contact-made-save">
                        {busy ? "Saving…" : "Save"}
                    </button>
                </div>
            )}
        </div>
    );
}


/* ---------- Initial Response modal ---------- */
function InitialResponseModal({ projectId, onClose }) {
    const [firstName, setFirstName] = useState("");
    const [note, setNote] = useState("");
    const [includeRfi, setIncludeRfi] = useState(false);
    const [rfiId, setRfiId] = useState("");
    const [linkedDraftId, setLinkedDraftId] = useState("");
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);
    // Authoritative server-rendered preview state. The admin MUST see and
    // confirm this exact projection before Send.
    const [preview, setPreview] = useState(null);
    const opId = useMemo(() => `ws1-ir-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, []);
    const buildBody = () => ({
        // The `ws1_initial_response` template's supported merge fields
        // (see `stage5_b4_seed.py::_INITIAL_MERGE`) are `first_name`,
        // `project_type`, `project_postcode`, `personal_note`,
        // `info_request_intro`, `info_request_cta`. Sending any other
        // key trips `render_communication_template`'s strict
        // "unsupported merge values" guard.
        merge_values: { first_name: firstName, personal_note: note },
        include_rfi: includeRfi,
        rfi_request_id: rfiId || null,
        linked_rfi_draft_id: linkedDraftId || null,
        create_op_id: opId,
    });
    const runPreview = async () => {
        setErr(""); setBusy(true);
        try {
            const r = await api.post(
                `/admin/projects/${projectId}/ws1/initial-response/preview`,
                buildBody());
            setPreview(r.data);
        } catch (e) {
            const d = e?.response?.data?.detail;
            setErr(typeof d === "string" ? d : (d?.error_class || "Preview failed"));
        } finally { setBusy(false); }
    };
    const confirmSend = async () => {
        if (!preview) return;
        setErr(""); setBusy(true);
        try {
            // Round-trip the previewed secure token so the sent email
            // carries the identical CTA the admin just approved (§4).
            await api.post(
                `/admin/projects/${projectId}/ws1/initial-response/send`,
                { ...buildBody(),
                  prepared_raw_token: preview.prepared_raw_token || null,
                  prepared_public_url: preview.prepared_public_url || null });
            toast.success("Initial response sent");
            onClose();
        } catch (e) {
            const d = e?.response?.data?.detail;
            setErr(typeof d === "string" ? d : (d?.error_class || "Send failed"));
        } finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="ir-modal">
            <div className="bg-white rounded-lg shadow-lg w-[620px] max-w-[92vw] max-h-[92vh] overflow-y-auto p-4 flex flex-col gap-3 border border-[#E5E2DC]">
                <div className="font-manrope font-semibold text-[15px]">Send Initial Response</div>
                {!preview && (
                    <>
                        <label className="flex flex-col gap-1">
                            <span className="text-[11px] uppercase text-[#666]">Client first name</span>
                            <input value={firstName} onChange={e => setFirstName(e.target.value)}
                                data-testid="ir-first-name" className="border border-[#E5E2DC] rounded-md px-2 py-1.5 text-[13px]" />
                        </label>
                        <label className="flex flex-col gap-1">
                            <span className="text-[11px] uppercase text-[#666]">Optional personal note</span>
                            <textarea rows={3} value={note} onChange={e => setNote(e.target.value)}
                                data-testid="ir-note" className="border border-[#E5E2DC] rounded-md px-2 py-1.5 text-[13px]" />
                        </label>
                        <label className="flex items-center gap-2 text-[13px]">
                            <input type="checkbox" checked={includeRfi} onChange={e => setIncludeRfi(e.target.checked)} data-testid="ir-include-rfi" />
                            Include Information Request CTA
                        </label>
                        {includeRfi && (
                            <div className="flex flex-col gap-2">
                                <label className="flex flex-col gap-1">
                                    <span className="text-[11px] uppercase text-[#666]">Already-issued RFI ID (optional)</span>
                                    <input value={rfiId} onChange={e => setRfiId(e.target.value)}
                                        data-testid="ir-rfi-id" className="border border-[#E5E2DC] rounded-md px-2 py-1.5 text-[13px]" />
                                </label>
                                <label className="flex flex-col gap-1">
                                    <span className="text-[11px] uppercase text-[#666]">Linked Draft RFI ID (issue on send)</span>
                                    <input value={linkedDraftId} onChange={e => setLinkedDraftId(e.target.value)}
                                        data-testid="ir-linked-draft-id" className="border border-[#E5E2DC] rounded-md px-2 py-1.5 text-[13px]" />
                                </label>
                            </div>
                        )}
                    </>
                )}
                {preview && (
                    <div className="flex flex-col gap-2 border border-[#E5E2DC] rounded-md p-3 bg-[#FBFAF6]" data-testid="ir-preview">
                        <div className="text-[11px] uppercase text-[#666]">Authoritative preview</div>
                        <div className="text-[12px]"><span className="text-[#666]">To:</span> {preview.recipient?.name} &lt;{preview.recipient?.address}&gt;</div>
                        <div className="text-[12px]"><span className="text-[#666]">From:</span> {preview.sender?.display}</div>
                        {preview.reply_to && <div className="text-[12px]"><span className="text-[#666]">Reply-To:</span> {preview.reply_to}</div>}
                        <div className="text-[12px]"><span className="text-[#666]">Subject:</span> <span data-testid="ir-preview-subject">{preview.rendered_subject}</span></div>
                        <pre className="whitespace-pre-wrap text-[12.5px] font-mono bg-white border border-[#E5E2DC] rounded-md p-2 max-h-[38vh] overflow-y-auto" data-testid="ir-preview-body">{preview.rendered_body}</pre>
                        {preview.prepared_public_url && (
                            <div className="text-[11px] text-[#666]">Linked RFI secure link included in body:
                                <span className="ml-1 break-all" data-testid="ir-preview-cta-url">{preview.prepared_public_url}</span>
                            </div>
                        )}
                        <div className="text-[10.5px] text-[#666]">Template {preview.template_code} · v{preview.template_version}</div>
                    </div>
                )}
                {err && <div className="text-[12px] text-terracotta" data-testid="ir-error">{err}</div>}
                <div className="flex justify-end gap-2">
                    {!preview && (
                        <>
                            <button onClick={onClose} disabled={busy} className="text-[13px] px-3 py-1.5 border border-[#E5E2DC] rounded-md" data-testid="ir-cancel">Cancel</button>
                            <button onClick={runPreview} disabled={busy || !firstName.trim()} className="text-[13px] px-3 py-1.5 bg-charcoal text-white rounded-md disabled:opacity-40" data-testid="ir-preview-btn">
                                {busy ? "Preparing…" : "Preview"}
                            </button>
                        </>
                    )}
                    {preview && (
                        <>
                            <button onClick={() => setPreview(null)} disabled={busy} className="text-[13px] px-3 py-1.5 border border-[#E5E2DC] rounded-md" data-testid="ir-preview-back">Back to edit</button>
                            <button onClick={confirmSend} disabled={busy} className="text-[13px] px-3 py-1.5 bg-charcoal text-white rounded-md disabled:opacity-40" data-testid="ir-send">
                                {busy ? "Sending…" : "Confirm & Send"}
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

/* ---------- Qualification modal ---------- */
function QualificationModal({ projectId, onClose }) {
    const [f, setF] = useState({
        project_objective: "", scope_clarification: "",
        budget_confidence: "approximate", target_timing_note: "",
        decision_makers: "", planning_note: "", existing_professionals: "",
        future_service_intention: "unsure", fsi_note: "",
        qualification_summary: "", lead_quality: "Medium",
        outcome: "ready_for_feasibility", next_action: "",
        sufficient_info_confirmed: false,
    });
    const upd = (k, v) => setF(s => ({ ...s, [k]: v }));
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);
    const opId = useMemo(() => `ws1-q-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, []);
    const save = async () => {
        setErr(""); setBusy(true);
        try {
            await api.post(`/admin/projects/${projectId}/ws1/qualification`,
                { ...f, create_op_id: opId });
            toast.success("Qualification completed");
            onClose();
        } catch (e) { setErr(readErrorDetail(e, "Save failed")); }
        finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="qual-modal">
            <div className="bg-white rounded-lg shadow-lg w-[640px] max-h-[90vh] overflow-y-auto max-w-[92vw] p-4 flex flex-col gap-3 border border-[#E5E2DC]">
                <div className="font-manrope font-semibold text-[15px]">Qualification Call</div>
                {[
                    ["project_objective", "Project objective / priorities"],
                    ["scope_clarification", "Scope clarification"],
                    ["target_timing_note", "Target timing / deadline notes"],
                    ["decision_makers", "Decision-makers / stakeholders"],
                    ["planning_note", "Planning / technical considerations"],
                    ["existing_professionals", "Existing professional / project info"],
                    ["qualification_summary", "Qualification summary"],
                    ["fsi_note", "Optional FSI context note"],
                    ["next_action", "Next action"],
                ].map(([k, l]) => (
                    <label key={k} className="flex flex-col gap-1">
                        <span className="text-[11px] uppercase text-[#666]">{l}</span>
                        <textarea rows={2} value={f[k]} onChange={e => upd(k, e.target.value)}
                            data-testid={`qual-${k}`} className="border border-[#E5E2DC] rounded-md px-2 py-1.5 text-[13px]" />
                    </label>
                ))}
                <label className="flex flex-col gap-1">
                    <span className="text-[11px] uppercase text-[#666]">Budget confidence</span>
                    <select value={f.budget_confidence} onChange={e => upd("budget_confidence", e.target.value)}
                        data-testid="qual-budget-confidence" className="border border-[#E5E2DC] rounded-md px-2 py-1.5 text-[13px]">
                        {BUDGET_CONF.map(v => <option key={v} value={v}>{v}</option>)}
                    </select>
                </label>
                <label className="flex flex-col gap-1">
                    <span className="text-[11px] uppercase text-[#666]">Future Service Intention</span>
                    <select value={f.future_service_intention} onChange={e => upd("future_service_intention", e.target.value)}
                        data-testid="qual-fsi" className="border border-[#E5E2DC] rounded-md px-2 py-1.5 text-[13px]">
                        {FSI_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                </label>
                <label className="flex flex-col gap-1">
                    <span className="text-[11px] uppercase text-[#666]">Lead Quality (admin-selected)</span>
                    <select value={f.lead_quality} onChange={e => upd("lead_quality", e.target.value)}
                        data-testid="qual-lead-quality" className="border border-[#E5E2DC] rounded-md px-2 py-1.5 text-[13px]">
                        {["High", "Medium", "Low"].map(v => <option key={v} value={v}>{v}</option>)}
                    </select>
                </label>
                <label className="flex flex-col gap-1">
                    <span className="text-[11px] uppercase text-[#666]">Qualification Outcome</span>
                    <select value={f.outcome} onChange={e => upd("outcome", e.target.value)}
                        data-testid="qual-outcome" className="border border-[#E5E2DC] rounded-md px-2 py-1.5 text-[13px]">
                        {OUTCOMES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                </label>
                <label className="flex items-center gap-2 text-[13px]">
                    <input type="checkbox" checked={f.sufficient_info_confirmed}
                        onChange={e => upd("sufficient_info_confirmed", e.target.checked)}
                        data-testid="qual-sufficient-info" />
                    Sufficient information for feasibility review
                </label>
                {err && <div className="text-[12px] text-terracotta" data-testid="qual-error">{err}</div>}
                <div className="flex justify-end gap-2">
                    <button onClick={onClose} disabled={busy}
                        className="text-[13px] px-3 py-1.5 border border-[#E5E2DC] rounded-md" data-testid="qual-cancel">Cancel</button>
                    <button onClick={save} disabled={busy}
                        className="text-[13px] px-3 py-1.5 bg-charcoal text-white rounded-md disabled:opacity-40" data-testid="qual-save">
                        {busy ? "Saving…" : "Save Qualification"}
                    </button>
                </div>
            </div>
        </div>
    );
}

/* ---------- RFI Authoring (549-01) ---------- */
export function RFIAuthoring({ projectId: propPid, initialDraft, onClose, onSaved, onIssued } = {}) {
    const params = useParams();
    // §Item2 (Modal Pass) — accept `projectId` as a prop when rendered
    //   inside the RFI list modal.  Falls back to `useParams` for
    //   backwards safety, but the standalone route has been removed
    //   so the prop path is the live one.
    const id = propPid ?? params.id;
    // §Multi-Item Pass — Item C: when the parent opens the modal for
    //   an existing Draft, `initialDraft` prefills the form and pins
    //   `rid` to the existing record so subsequent saves PATCH it
    //   instead of creating a duplicate.
    const [title, setTitle] = useState(initialDraft?.title ?? "");
    const [intro, setIntro] = useState(initialDraft?.intro ?? "");
    const [workstage, setWorkstage] = useState(initialDraft?.linked_workstage ?? 1);
    const [due, setDue] = useState(
        initialDraft?.due_at ? String(initialDraft.due_at).slice(0, 10) : "");
    const [items, setItems] = useState(
        initialDraft?.items
            ? initialDraft.items.map(it => ({
                id: it.id,
                title: it.title || "",
                guidance: it.guidance || "",
                required: !!it.required,
                allow_not_available: !!it.allow_not_available,
                response_type: it.response_type,
                order: it.order,
            }))
            : []);
    const [rid, setRid] = useState(initialDraft?.id ?? null);
    const [issued, setIssued] = useState(null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const opId = useMemo(() => `rfi-issue-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, []);
    // §Item2 — create_op_id is required by RFIDraftBody. Kept stable
    //          across the mount so retries of the same draft POST are
    //          idempotent (bind_op_lock / activity-log dedup remain
    //          safe). Regenerated only on a fresh open of the modal.
    const createOpId = useMemo(() => `rfi-create-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, []);
    const addItem = () => setItems([...items, {
        title: "", guidance: "", required: false,
        allow_not_available: false, response_type: "single_file", order: items.length + 1,
    }]);
    const rmItem = (i) => setItems(items.filter((_, idx) => idx !== i));
    const moveItem = (i, dir) => {
        const j = i + dir;
        if (j < 0 || j >= items.length) return;
        const n = items.slice(); [n[i], n[j]] = [n[j], n[i]];
        setItems(n.map((it, k) => ({ ...it, order: k + 1 })));
    };
    const upd = (i, k, v) => setItems(items.map((it, idx) => idx === i ? { ...it, [k]: v } : it));

    const persist = async () => {
        setErr("");
        try {
            if (rid) {
                // §Multi-Item Pass — Item D fix: the PATCH route is
                //   registered at `/admin/information-requests/{rid}`
                //   (no `/projects/{pid}/` segment).  Prior URL
                //   included the project id and always returned 404
                //   after the first save.  `create_op_id` is not part
                //   of RFIUpdateBody, so it is omitted on update.
                await api.patch(`/admin/information-requests/${rid}`,
                    { title, intro, linked_workstage: workstage, due_at: due || null, items });
                return rid;
            }
            const r = await api.post(`/admin/projects/${id}/information-requests`,
                { title, intro, linked_workstage: workstage, due_at: due || null, items,
                  create_op_id: createOpId });
            setRid(r.data.item.id);
            return r.data.item.id;
        } catch (e) { setErr(readErrorDetail(e, "Save failed")); throw e; }
    };
    const saveDraft = async () => {
        setBusy(true);
        try {
            const savedRid = await persist();
            toast.success("Draft saved");
            // §Modal Pass — mirror the existing pattern (this same handler
            //   pre-refactor kept the form open on save; B5Pages.jsx:364
            //   uses the same toast-and-stay behaviour).  Parent may
            //   refresh the underlying list silently while the modal
            //   stays open so the admin can iterate on the same draft.
            if (onSaved) onSaved(savedRid);
        } catch {} finally { setBusy(false); }
    };
    const [preview, setPreview] = useState(null);
    const previewClient = async () => {
        setErr(""); setBusy(true);
        try {
            const useRid = await persist();
            const r = await api.get(`/admin/information-requests/${useRid}/preview`);
            setPreview(r.data);
        } catch (e) {
            const d = e?.response?.data?.detail;
            setErr(typeof d === "string" ? d : "Preview failed");
        } finally { setBusy(false); }
    };
    const issue = async () => {
        setBusy(true);
        try {
            const useRid = await persist();
            const r = await api.post(`/admin/information-requests/${useRid}/issue`,
                { op_id: opId });
            setIssued(r.data.item);
            toast.success("Request issued");
            // §Modal Pass — parent closes the modal and refreshes the
            //   list so the newly-issued RFI appears in the table.
            if (onIssued) onIssued(r.data.item);
        } catch (e) { setErr(readErrorDetail(e, "Issue failed")); }
        finally { setBusy(false); }
    };
    return (
        <div className="flex flex-col gap-4" data-testid="rfi-authoring">
            <section className="bg-white border border-[#E5E2DC] rounded-lg p-5 flex flex-col gap-3" data-testid="rfi-auth-request-details">
                <div className="font-manrope font-semibold text-[13px]">Request Details</div>
                <label className="flex flex-col gap-1">
                    <span className="text-[11px] uppercase text-[#666]">Request title *</span>
                    <input value={title} onChange={e => setTitle(e.target.value)} data-testid="rfi-title"
                        className="border border-[#E5E2DC] rounded-md px-2 py-1.5 text-[13px]" />
                </label>
                <label className="flex flex-col gap-1">
                    <span className="text-[11px] uppercase text-[#666]">Client-facing introduction</span>
                    <textarea rows={3} value={intro} onChange={e => setIntro(e.target.value)} data-testid="rfi-intro"
                        className="border border-[#E5E2DC] rounded-md px-2 py-1.5 text-[13px]" />
                </label>
                <div className="grid grid-cols-2 gap-3">
                    <label className="flex flex-col gap-1">
                        <span className="text-[11px] uppercase text-[#666]">Linked Workstage</span>
                        <select value={workstage} onChange={e => setWorkstage(Number(e.target.value))} data-testid="rfi-workstage"
                            className="border border-[#E5E2DC] rounded-md px-2 py-1.5 text-[13px]">
                            {[1, 2, 3, 4, 5, 6, 7].map(w => <option key={w} value={w}>WS{w}</option>)}
                        </select>
                    </label>
                    <label className="flex flex-col gap-1">
                        <span className="text-[11px] uppercase text-[#666]">Due date</span>
                        <input type="date" value={due} onChange={e => setDue(e.target.value)} data-testid="rfi-due"
                            className="border border-[#E5E2DC] rounded-md px-2 py-1.5 text-[13px]" />
                    </label>
                </div>
            </section>

            <section className="bg-white border border-[#E5E2DC] rounded-lg p-5 flex flex-col gap-3" data-testid="rfi-auth-requested-items">
                <div className="flex items-center justify-between">
                    <div className="font-manrope font-semibold text-[13px]">Requested Items</div>
                    <button onClick={addItem} data-testid="rfi-add-item"
                        className="text-[12px] px-2.5 py-1 border border-[#E5E2DC] rounded-md flex items-center gap-1">
                        <Plus className="w-3 h-3" /> Add Requested Item
                    </button>
                </div>
                {items.length === 0 && <div className="text-[12px] text-[#666]">No items yet.</div>}
                {items.map((it, i) => (
                    <div key={i} className="border border-[#E5E2DC] rounded-md p-3 flex flex-col gap-2" data-testid={`rfi-item-${i}`}>
                        <div className="flex items-center gap-2">
                            <div className="text-[12px] text-[#666]">{i + 1}.</div>
                            <input value={it.title} onChange={e => upd(i, "title", e.target.value)}
                                data-testid={`rfi-item-title-${i}`} placeholder="Item title"
                                className="flex-1 border border-[#E5E2DC] rounded-md px-2 py-1.5 text-[13px]" />
                            <button onClick={() => moveItem(i, -1)} data-testid={`rfi-item-up-${i}`}><ChevronUp className="w-4 h-4" /></button>
                            <button onClick={() => moveItem(i, 1)} data-testid={`rfi-item-down-${i}`}><ChevronDown className="w-4 h-4" /></button>
                            <button onClick={() => rmItem(i)} data-testid={`rfi-item-remove-${i}`}><Trash2 className="w-4 h-4 text-terracotta" /></button>
                        </div>
                        <textarea rows={2} value={it.guidance} onChange={e => upd(i, "guidance", e.target.value)}
                            data-testid={`rfi-item-guidance-${i}`} placeholder="Item-specific guidance"
                            className="border border-[#E5E2DC] rounded-md px-2 py-1.5 text-[13px]" />
                        <div className="flex flex-wrap items-center gap-3 text-[12px]">
                            {/* §Multi-Item Pass — Item A: `Required`
                                checkbox removed.  Admin dispositions
                                each item after the client responds
                                (Accept / Request Clarification /
                                Mark NA); `required` is no longer
                                authoritative. */}
                            <label className="flex items-center gap-1">
                                <input type="checkbox" checked={it.allow_not_available} onChange={e => upd(i, "allow_not_available", e.target.checked)} data-testid={`rfi-item-nap-${i}`} />
                                Allow Not Available
                            </label>
                            <select value={it.response_type} onChange={e => upd(i, "response_type", e.target.value)}
                                data-testid={`rfi-item-type-${i}`}
                                className="border border-[#E5E2DC] rounded-md px-2 py-1 text-[12px]">
                                {RESPONSE_TYPES.map(rt => <option key={rt.value} value={rt.value}>{rt.label}</option>)}
                            </select>
                        </div>
                    </div>
                ))}
            </section>

            {issued && (
                <section className="bg-[#F5F1E8] border border-[#E5E2DC] rounded-lg p-4 flex flex-col gap-2" data-testid="rfi-issued-summary">
                    <div className="font-manrope font-semibold text-[13px]">Request Issued</div>
                    <div className="text-[12px]">Public URL: <a href={issued.public_url} target="_blank" rel="noreferrer" className="underline" data-testid="rfi-public-url">{issued.public_url}</a></div>
                </section>
            )}
            {err && <div className="text-[13px] text-terracotta" data-testid="rfi-auth-error">{err}</div>}

            <div className="flex justify-end gap-2">
                <button onClick={previewClient} disabled={busy || !title.trim() || items.length === 0}
                    data-testid="rfi-preview-client"
                    className="text-[13px] px-3 py-1.5 border border-[#E5E2DC] rounded-md disabled:opacity-40">
                    Preview Client Request
                </button>
                <button onClick={saveDraft} disabled={busy || !title.trim()}
                    data-testid="rfi-save-draft"
                    className="text-[13px] px-3 py-1.5 border border-[#E5E2DC] rounded-md disabled:opacity-40">
                    Save Draft
                </button>
                <button onClick={issue} disabled={busy || !title.trim() || items.length === 0 || !!issued}
                    data-testid="rfi-issue"
                    className="text-[13px] px-3 py-1.5 bg-charcoal text-white rounded-md disabled:opacity-40">
                    Issue Information Request
                </button>
            </div>
            {preview && <RFIClientPreviewModal preview={preview} onClose={() => setPreview(null)} />}
        </div>
    );
}

/* Read-only, side-effect-free preview of the client-safe RFI projection.
   Uses the same visual language as other B4 dialogs; no native prompts. */
function RFIClientPreviewModal({ preview, onClose }) {
    const p = preview?.preview || {};
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="rfi-client-preview-modal">
            <div className="bg-white rounded-lg shadow-lg w-[640px] max-w-[92vw] max-h-[92vh] overflow-y-auto p-5 flex flex-col gap-3 border border-[#E5E2DC]">
                <div className="flex items-center justify-between">
                    <div className="font-manrope font-semibold text-[15px]">Preview — Client Request</div>
                    <div className="text-[11px] uppercase text-[#666]" data-testid="rfi-preview-status">{preview?.status}</div>
                </div>
                <div className="text-[12px] text-[#666]">This is exactly what the client will see. It is a read-only preview — nothing has been issued.</div>
                <div className="flex flex-col gap-1 border border-[#E5E2DC] rounded-md p-3 bg-[#FBFAF6]">
                    <div className="text-[15px] font-serif" data-testid="rfi-preview-title">{p.title}</div>
                    {p.intro && <div className="text-[13px] whitespace-pre-wrap" data-testid="rfi-preview-intro">{p.intro}</div>}
                    <div className="text-[11px] uppercase text-[#666]">
                        Linked WS{p.linked_workstage ?? "–"}{p.due_at ? ` · Due ${String(p.due_at).slice(0, 10)}` : ""}
                    </div>
                </div>
                <div className="flex flex-col gap-2">
                    {(p.items || []).map((it, i) => (
                        <div key={it.id || i} className="border border-[#E5E2DC] rounded-md p-3 flex flex-col gap-1" data-testid={`rfi-preview-item-${i}`}>
                            <div className="text-[13px] font-medium">
                                {i + 1}. {it.title}
                                <span className="ml-2 text-[10.5px] uppercase text-[#666]">
                                    {it.required ? "Required" : "Optional"}
                                    {it.allow_not_available ? " · Not-available allowed" : ""}
                                </span>
                            </div>
                            {it.guidance && <div className="text-[12px] text-[#333] whitespace-pre-wrap">{it.guidance}</div>}
                            <div className="text-[10.5px] uppercase text-[#666]">Response · {it.response_type}</div>
                        </div>
                    ))}
                    {(p.items || []).length === 0 && (
                        <div className="text-[12px] text-[#666]">No items yet.</div>
                    )}
                </div>
                <div className="flex justify-end">
                    <button onClick={onClose} className="text-[13px] px-3 py-1.5 bg-charcoal text-white rounded-md" data-testid="rfi-preview-close">Close</button>
                </div>
            </div>
        </div>
    );
}

/* ---------- RFI Review (549-02) ---------- */
/* §Multi-Item Pass — Item C: RFIReviewPanel is the prop-driven,
   modal-friendly form of the former `RFIReview` page.  It renders the
   Request Summary + Requested Items Review + Complete Request panels
   without any page-shell wrapper (parent modal supplies padding and
   scroll).  Route `/rfi/:rid` has been removed; the parent RFIList
   modal now consumes this component directly.

   Cancel Request has been removed — deletion is now handled by the
   parent modal's Delete flow. */
export function RFIReviewPanel({ projectId: propPid, rid: propRid, onRefresh } = {}) {
    const params = useParams();
    const id = propPid ?? params.id;
    const rid = propRid ?? params.rid;
    const [row, setRow] = useState(null);
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);
    const [clarifyItem, setClarifyItem] = useState(null);
    const [editingDue, setEditingDue] = useState(false);
    const [newDue, setNewDue] = useState("");
    const load = async () => {
        try {
            const r = await api.get(`/admin/information-requests/${rid}`);
            const rowData = (r.data && r.data.item) ? r.data.item : r.data;
            setRow(rowData);
            if (onRefresh) onRefresh(rowData);
        } catch (e) { setErr(readErrorDetail(e, "Load failed")); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [rid]);
    const submitDueEdit = async () => {
        setBusy(true);
        try {
            await api.patch(`/admin/information-requests/${rid}/due-at`, {
                due_at: newDue || null,
                op_id: `rfi-due-${rid}-${Date.now()}`,
            });
            toast.success("Deadline revised");
            setEditingDue(false); setNewDue(""); load();
        } catch (e) {
            toast.error(readErrorDetail(e, "Failed"));
        } finally { setBusy(false); }
    };
    const review = async (itemId, outcome, mark_na) => {
        setBusy(true);
        try {
            await api.post(`/admin/information-requests/${rid}/items/${itemId}/review`,
                { outcome, mark_na: !!mark_na });
            toast.success("Item reviewed");
            load();
        } catch (e) { toast.error(readErrorDetail(e, "Failed")); }
        finally { setBusy(false); }
    };
    const complete = async () => {
        setBusy(true);
        try {
            const opId = `rfi-complete-${rid}-${Date.now()}`;
            await api.post(`/admin/information-requests/${rid}/complete`,
                { op_id: opId });
            toast.success("Request completed");
            load();
        } catch (e) {
            const d = e?.response?.data?.detail;
            const blockers = d && Array.isArray(d.blockers) ? d.blockers : null;
            toast.error(
                blockers && blockers.length
                    ? blockers.join("; ")
                    : readErrorDetail(e, "Failed"),
            );
        } finally { setBusy(false); }
    };
    if (err) return <div className="p-4 text-terracotta text-[13px]" data-testid="rfi-review-error">{err}</div>;
    if (!row) return <div className="p-4 text-[#666] text-[13px]" data-testid="rfi-review-loading">Loading…</div>;

    // §Multi-Item Pass — Item C client-side predicate: Complete
    //   button enabled only when every item is Accepted or Not
    //   Applicable.  Server-side `completion_blockers` mirrors this
    //   universally (no more `required` short-circuit).
    const REVIEW_ACCEPTED = "Accepted";
    const REVIEW_NA = "Not Applicable";
    const canComplete = row.status !== "Complete"
        && (row.items || []).every(
            it => it.admin_review_state === REVIEW_ACCEPTED
                  || it.admin_review_state === REVIEW_NA);

    return (
        <div className="flex flex-col gap-4" data-testid="rfi-review">
            <section className="bg-white border border-[#E5E2DC] rounded-lg p-5 flex flex-col gap-2" data-testid="rfi-request-summary">
                <div className="font-manrope font-semibold text-[13px]">Request Summary</div>
                <div className="text-[13px]"><b>{row.title}</b></div>
                <div className="text-[12px] text-[#666]">
                    Status: <span data-testid="rfi-status">{row.status}</span> ·
                    Linked WS{row.linked_workstage} ·
                    Issued {row.issued_at || "—"} ·
                    Due <span data-testid="rfi-due-display">{row.due_at || "—"}</span> ·
                    Submitted {row.submitted_at || "—"}
                    {(row.status === "Issued" || row.status === "Submitted") && (
                        <button
                            onClick={() => {
                                setNewDue(row.due_at ? row.due_at.slice(0, 10) : "");
                                setEditingDue(true);
                            }}
                            data-testid="rfi-edit-due"
                            className="ml-2 text-[12px] text-[#A8543F] underline">
                            Edit due date
                        </button>
                    )}
                </div>
                <div className="text-[12px] text-[#666]">
                    Client access: token · expires {row.client_access?.expires_at || "—"} · reissued {row.client_access?.reissued || 0}×
                </div>
            </section>

            <section className="bg-white border border-[#E5E2DC] rounded-lg p-5 flex flex-col gap-3" data-testid="rfi-review-items">
                <div className="font-manrope font-semibold text-[13px]">Requested Items Review</div>
                {row.items.map((it, i) => (
                    <div key={it.id} className="border border-[#E5E2DC] rounded-md p-3 flex flex-col gap-2" data-testid={`rfi-review-item-${i}`}>
                        <div className="flex items-center justify-between">
                            {/* §Multi-Item Pass — Item A: `*` required
                                marker removed; admins disposition each
                                item after the client responds. */}
                            <div className="text-[13px] font-semibold">{i + 1}. {it.title}</div>
                            <div className="text-[11px] uppercase text-[#666]">{it.response_type}</div>
                        </div>
                        {it.guidance && <div className="text-[12px] text-[#666] italic">{it.guidance}</div>}
                        <div className="grid grid-cols-2 gap-3 text-[12px]">
                            <div>
                                <div className="text-[11px] uppercase text-[#999]">Client Response State</div>
                                <div data-testid={`rfi-review-client-state-${i}`}>{it.client_state}</div>
                                {it.response_text && <div className="whitespace-pre-wrap mt-1">{it.response_text}</div>}
                                {it.response_files?.length > 0 && <div className="mt-1 text-[11px] text-[#666]">Files: {it.response_files.filter(f => f.state === "current").length}</div>}
                            </div>
                            <div>
                                <div className="text-[11px] uppercase text-[#999]">Admin Review State</div>
                                <div data-testid={`rfi-review-admin-state-${i}`}>{it.admin_review_state}</div>
                                {it.clarification_note && <div className="whitespace-pre-wrap mt-1 text-terracotta">{it.clarification_note}</div>}
                            </div>
                        </div>
                        {row.status !== "Complete" && (() => {
                            // §Polish Pass — Item 1: active-state
                            //   treatment for per-item buttons.  The
                            //   button matching `admin_review_state`
                            //   renders solid terracotta with white
                            //   text.  Clicking the currently-active
                            //   button is a no-op — use Clear Status
                            //   below to reset the disposition.
                            const REVIEW_ACCEPTED = "Accepted";
                            const REVIEW_CLARIFICATION = "Clarification Required";
                            const REVIEW_NA = "Not Applicable";
                            const state = it.admin_review_state;
                            const isAccepted = state === REVIEW_ACCEPTED;
                            const isClarification = state === REVIEW_CLARIFICATION;
                            const isNA = state === REVIEW_NA;
                            const hasStatus = isAccepted || isClarification || isNA;
                            const cls = (active) => `text-[12px] px-2.5 py-1 rounded-md border ${
                                active
                                    ? "bg-terracotta text-white border-terracotta hover:bg-terracotta-600"
                                    : "bg-white text-charcoal border-[#E5E2DC] hover:bg-[#FAFAFA]"
                            }`;
                            return (
                                <div className="flex flex-col gap-1 pt-1">
                                    <div className="flex gap-2">
                                        <button onClick={() => !isAccepted && review(it.id, "accept")}
                                            disabled={busy}
                                            aria-pressed={isAccepted}
                                            data-testid={`rfi-item-accept-${i}`}
                                            className={cls(isAccepted)}>Accept</button>
                                        <button onClick={() => !isClarification && setClarifyItem(it)}
                                            disabled={busy}
                                            aria-pressed={isClarification}
                                            data-testid={`rfi-item-clarify-${i}`}
                                            className={cls(isClarification)}>Request Clarification</button>
                                        {it.allow_not_available && (
                                            <button onClick={() => !isNA && review(it.id, "mark_na")}
                                                disabled={busy}
                                                aria-pressed={isNA}
                                                data-testid={`rfi-item-mark-na-${i}`}
                                                className={cls(isNA)}>Mark NA</button>
                                        )}
                                    </div>
                                    {/* §Polish Pass — Item 2: Clear
                                        Status only appears when the
                                        item carries a disposition.
                                        Clicking POSTs `outcome=clear`
                                        which resets admin_review_state
                                        to `Pending Review`.  No confirm. */}
                                    {hasStatus && (
                                        <button onClick={() => review(it.id, "clear")}
                                            disabled={busy}
                                            data-testid={`rfi-item-clear-${i}`}
                                            className="self-start text-[11px] text-[#666] hover:text-terracotta underline">
                                            Clear status
                                        </button>
                                    )}
                                </div>
                            );
                        })()}
                    </div>
                ))}
            </section>

            {row.status !== "Complete" && (
                <section className="bg-white border border-[#E5E2DC] rounded-lg p-5 flex flex-col gap-2" data-testid="rfi-complete-section">
                    <div className="font-manrope font-semibold text-[13px]">Complete Information Request</div>
                    {!canComplete && (row.completion_blockers || []).length > 0 && (
                        <ul className="text-[12px] text-terracotta list-disc pl-5" data-testid="rfi-complete-blockers">
                            {row.completion_blockers.map((b, i) => <li key={i}>{b}</li>)}
                        </ul>
                    )}
                    <button onClick={complete} disabled={busy || !canComplete}
                        data-testid="rfi-complete"
                        className="self-start text-[13px] px-3 py-1.5 bg-charcoal text-white rounded-md disabled:opacity-40">
                        Complete Request
                    </button>
                </section>
            )}

            {clarifyItem && <ClarifyDialog item={clarifyItem} rid={rid}
                onClose={(reload) => { setClarifyItem(null); if (reload) load(); }} />}
            {editingDue && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="rfi-edit-due-modal">
                    <div className="bg-white rounded-lg shadow-lg w-[420px] p-4 flex flex-col gap-3 border border-[#E5E2DC]">
                        <div className="font-manrope font-semibold text-[13px]">Revise Request Due Date</div>
                        <label className="flex flex-col gap-1">
                            <span className="text-[11px] uppercase text-[#666]">New due date</span>
                            <input type="date" value={newDue}
                                onChange={e => setNewDue(e.target.value)}
                                data-testid="rfi-edit-due-input"
                                className="border border-[#E5E2DC] rounded-md px-2 py-1.5 text-[13px]" />
                        </label>
                        <div className="flex justify-end gap-2">
                            <button onClick={() => { setEditingDue(false); setNewDue(""); }} disabled={busy}
                                className="text-[13px] px-3 py-1.5 border border-[#E5E2DC] rounded-md" data-testid="rfi-edit-due-cancel">Cancel</button>
                            <button onClick={submitDueEdit} disabled={busy}
                                className="text-[13px] px-3 py-1.5 bg-charcoal text-white rounded-md disabled:opacity-40"
                                data-testid="rfi-edit-due-submit">Save</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function ClarifyDialog({ item, rid, onClose }) {
    const [note, setNote] = useState("");
    const [due, setDue] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const save = async () => {
        setBusy(true); setErr("");
        try {
            await api.post(`/admin/information-requests/${rid}/items/${item.id}/review`,
                { outcome: "request_clarification", clarification_note: note,
                  revised_due_at: due || null });
            toast.success("Clarification requested");
            onClose(true);
        } catch (e) { setErr(readErrorDetail(e, "Failed")); }
        finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="rfi-clarify-modal">
            <div className="bg-white rounded-lg shadow-lg w-[480px] max-w-[92vw] p-4 flex flex-col gap-3 border border-[#E5E2DC]">
                <div className="font-manrope font-semibold text-[13px]">Request Clarification — {item.title}</div>
                <textarea rows={4} value={note} onChange={e => setNote(e.target.value)}
                    placeholder="Client-facing clarification note"
                    data-testid="rfi-clarify-note"
                    className="border border-[#E5E2DC] rounded-md px-2 py-1.5 text-[13px]" />
                <label className="flex flex-col gap-1">
                    <span className="text-[11px] uppercase text-[#666]">Optional revised due date</span>
                    <input type="date" value={due} onChange={e => setDue(e.target.value)}
                        data-testid="rfi-clarify-due"
                        className="border border-[#E5E2DC] rounded-md px-2 py-1.5 text-[13px]" />
                </label>
                {err && <div className="text-[12px] text-terracotta">{err}</div>}
                <div className="flex justify-end gap-2">
                    <button onClick={() => onClose(false)} disabled={busy}
                        className="text-[13px] px-3 py-1.5 border border-[#E5E2DC] rounded-md" data-testid="rfi-clarify-cancel">Cancel</button>
                    <button onClick={save} disabled={busy || !note.trim()}
                        className="text-[13px] px-3 py-1.5 bg-charcoal text-white rounded-md disabled:opacity-40"
                        data-testid="rfi-clarify-save">Send Clarification</button>
                </div>
            </div>
        </div>
    );
}

/* §Multi-Item Pass — Item C: CancelDialog removed with the "Cancel
   Request" UI trigger.  Cancellation of RFIs is superseded by
   deletion, gated by "no client response" (Draft always deletable). */


/* ---------- RFI List (Item 1 — project-scoped) ---------------------- */
/* §Item1 / §Multi-Item Pass Items B, C, D: Lists all RFIs for a
   project.  Two filter groups (Issue Status + RFI Progress, both
   multi-select, second group disabled when Issued is not selected),
   row-click opens a unified modal that switches between Draft-mode
   (RFIAuthoring) and Issued-mode (RFIReviewPanel).  Delete link with
   its own confirmation dialog lives inside the modal footer.
   `Cancelled` is no longer a filterable status.  Sort by created_at,
   toggled from the Date column header.  No pagination in this pass. */
const RFI_ISSUE_STATUS_CHIPS = [
    { key: "Draft", label: "Draft" },
    { key: "Issued", label: "Issued" },
];

const RFI_PROGRESS_CHIPS = [
    { key: "Partially Received", label: "Partial" },
    { key: "Submitted", label: "Submitted" },
    { key: "Clarification Requested", label: "Clarification" },
    { key: "Complete", label: "Complete" },
];

const RFI_STATUS_TONE = {
    "Draft":                  "bg-stone-100 text-stone-800 border-stone-300",
    "Issued":                 "bg-amber-100 text-amber-900 border-amber-300",
    "Partially Received":     "bg-sky-100 text-sky-900 border-sky-300",
    "Submitted":              "bg-indigo-100 text-indigo-900 border-indigo-300",
    "Clarification Requested":"bg-rose-100 text-rose-900 border-rose-300",
    "Complete":               "bg-emerald-100 text-emerald-900 border-emerald-300",
};

export function RFIList() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    usePageMeta({ title: "RFIs · Project | Studio G4 Admin" });
    // §Item B — two independent multi-select groups.  Empty set = "no
    //   filter for this group".  Group 2 (progress) chips are ignored
    //   when Issued is NOT selected in Group 1 (the UI disables them).
    const [issueSel, setIssueSel] = useState(new Set());
    const [progressSel, setProgressSel] = useState(new Set());
    const [sortDir, setSortDir] = useState("date_desc");
    const [err, setErr] = useState("");
    const [showNew, setShowNew] = useState(false);
    const [openRid, setOpenRid] = useState(null);      // selected RFI id
    const [openMode, setOpenMode] = useState(null);    // "draft" | "issued"
    const [openInitial, setOpenInitial] = useState(null); // draft prefill
    const [newKey, setNewKey] = useState(0);
    const progressEnabled = issueSel.has("Issued");

    const statusParam = useMemo(() => {
        const vals = new Set(issueSel);
        if (progressEnabled) {
            for (const v of progressSel) vals.add(v);
        }
        return Array.from(vals).join(",");
    }, [issueSel, progressSel, progressEnabled]);

    const load = useCallback(() => {
        let cancelled = false;
        setLoading(true); setErr("");
        api.get(`/admin/projects/${id}/information-requests`,
                { params: { status: statusParam || undefined, sort: sortDir } })
            .then(r => { if (!cancelled) setRows(r.data?.items || []); })
            .catch(e => { if (!cancelled) setErr(readErrorDetail(e, "Could not load requests")); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [id, statusParam, sortDir]);
    useEffect(() => { return load(); }, [load]);
    const toggleSort = () => setSortDir(prev => prev === "date_desc" ? "date_asc" : "date_desc");
    const toggleFrom = (setter) => (key) => setter(prev => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key); else next.add(key);
        return next;
    });
    const toggleIssue = toggleFrom(setIssueSel);
    const toggleProgress = toggleFrom(setProgressSel);

    const openNew = () => { setNewKey(k => k + 1); setShowNew(true); };
    const closeNew = () => setShowNew(false);
    const openRow = (r) => {
        if (r.status === "Draft") {
            setOpenMode("draft");
            setOpenInitial(r);
        } else {
            setOpenMode("issued");
            setOpenInitial(null);
        }
        setOpenRid(r.id);
    };
    const closeRow = () => { setOpenRid(null); setOpenMode(null); setOpenInitial(null); };
    // Escape closes whichever modal is on top (New RFI takes
    // precedence — matches the last-opened one).
    useEffect(() => {
        if (!showNew && !openRid) return undefined;
        const onKey = (e) => {
            if (e.key !== "Escape") return;
            if (showNew) closeNew();
            else if (openRid) closeRow();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [showNew, openRid]);

    // For Issued-mode delete gating we need a fresh copy of the RFI
    // (with items and client_state).  RFIReviewPanel fetches this
    // internally; we mirror it here via `openRow`'s row payload as a
    // conservative baseline and refresh on `onRefresh`.
    const [openRow_, setOpenRow_] = useState(null);
    useEffect(() => {
        // Reset the mirrored row when the modal closes or the target
        // changes so stale gate flags never flash.
        setOpenRow_(null);
    }, [openRid]);

    return (
        <div className="flex flex-col gap-4" data-testid="rfi-list-page">
            <button onClick={() => navigate(`/admin/projects/${id}`)}
                data-testid="rfi-list-back"
                className="self-start inline-flex items-center gap-1.5 text-[13px] text-[#666] hover:text-charcoal">
                <ArrowLeft className="w-4 h-4" /> Back to workspace
            </button>
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-serif text-charcoal">Request Information</h1>
                <button onClick={openNew}
                    data-testid="rfi-list-new"
                    className="inline-flex items-center gap-1.5 text-[13px] px-3 py-1.5 bg-charcoal text-white rounded-md">
                    <Plus className="w-4 h-4" /> New RFI
                </button>
            </div>

            <div className="flex flex-col gap-3" data-testid="rfi-list-filters">
                <div className="flex flex-col gap-1">
                    <div className="text-[11px] uppercase tracking-wide font-manrope font-semibold text-[#666]">Issue Status</div>
                    <div className="flex flex-wrap items-center gap-2" data-testid="rfi-list-issue-chips">
                        {RFI_ISSUE_STATUS_CHIPS.map(c => {
                            const active = issueSel.has(c.key);
                            return (
                                <button key={c.key}
                                    onClick={() => toggleIssue(c.key)}
                                    data-testid={`rfi-chip-issue-${c.key.toLowerCase().replace(/\s+/g, "-")}`}
                                    aria-pressed={active}
                                    className={`text-[12px] px-2.5 py-1 rounded-full border transition ${
                                        active
                                            ? "bg-charcoal text-white border-charcoal"
                                            : "bg-white text-charcoal border-[#E5E2DC] hover:bg-[#FAFAFA]"
                                    }`}>
                                    {c.label}
                                </button>
                            );
                        })}
                    </div>
                </div>
                <div className="flex flex-col gap-1">
                    <div className={`text-[11px] uppercase tracking-wide font-manrope font-semibold ${progressEnabled ? "text-[#666]" : "text-[#C4C0B8]"}`}>
                        RFI Progress
                    </div>
                    <div className="flex flex-wrap items-center gap-2" data-testid="rfi-list-progress-chips">
                        {RFI_PROGRESS_CHIPS.map(c => {
                            const active = progressEnabled && progressSel.has(c.key);
                            const base = "text-[12px] px-2.5 py-1 rounded-full border transition";
                            const cls = !progressEnabled
                                ? "bg-[#F5F3EE] text-[#B8B4AB] border-[#E5E2DC] cursor-not-allowed"
                                : active
                                    ? "bg-charcoal text-white border-charcoal"
                                    : "bg-white text-charcoal border-[#E5E2DC] hover:bg-[#FAFAFA]";
                            return (
                                <button key={c.key}
                                    onClick={() => progressEnabled && toggleProgress(c.key)}
                                    disabled={!progressEnabled}
                                    data-testid={`rfi-chip-progress-${c.key.toLowerCase().replace(/\s+/g, "-")}`}
                                    aria-pressed={active}
                                    className={`${base} ${cls}`}>
                                    {c.label}
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>

            {err && <div className="text-[13px] text-terracotta" data-testid="rfi-list-error">{err}</div>}

            <div className="bg-white border border-[#E5E2DC] rounded-lg overflow-hidden">
                <table className="w-full text-[13px]" data-testid="rfi-list-table">
                    <thead className="bg-[#FBFAF6] text-[11px] uppercase tracking-wide text-[#666]">
                        <tr>
                            <th className="text-left px-3 py-2 w-[140px]">
                                <button onClick={toggleSort}
                                    data-testid="rfi-list-sort-date"
                                    aria-label={`Sort by date, currently ${sortDir === "date_desc" ? "newest first" : "oldest first"}`}
                                    className="inline-flex items-center gap-1 uppercase">
                                    Date
                                    {sortDir === "date_desc"
                                        ? <ChevronDown className="w-3.5 h-3.5" />
                                        : <ChevronUp className="w-3.5 h-3.5" />}
                                </button>
                            </th>
                            <th className="text-left px-3 py-2">Title</th>
                            <th className="text-left px-3 py-2 w-[160px]">Status</th>
                            <th className="text-left px-3 py-2 w-[80px]">Items</th>
                            <th className="text-left px-3 py-2 w-[110px]">Due</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr><td colSpan={5} className="px-3 py-6 text-center text-[#666]" data-testid="rfi-list-loading">Loading…</td></tr>
                        )}
                        {!loading && rows.length === 0 && (
                            <tr>
                                <td colSpan={5} className="px-3 py-8 text-center" data-testid="rfi-list-empty">
                                    <div className="flex flex-col items-center gap-2 text-[13px] text-[#666]">
                                        <div>No RFIs yet.</div>
                                        <button onClick={openNew}
                                            data-testid="rfi-list-empty-new"
                                            className="text-[12px] px-2.5 py-1 border border-[#E5E2DC] rounded-md">
                                            New RFI
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        )}
                        {!loading && rows.map(r => {
                            const date = (r.created_at || "").slice(0, 10);
                            const due = r.due_at ? String(r.due_at).slice(0, 10) : "—";
                            const tone = RFI_STATUS_TONE[r.status] || "bg-white text-charcoal border-[#E5E2DC]";
                            return (
                                <tr key={r.id}
                                    tabIndex={0}
                                    onClick={() => openRow(r)}
                                    onKeyDown={(e) => { if (e.key === "Enter") openRow(r); }}
                                    className="border-t border-[#F1EEE8] hover:bg-[#FBFAF6] cursor-pointer focus:outline-none focus:bg-[#FBFAF6]"
                                    data-testid={`rfi-list-row-${r.id}`}>
                                    <td className="px-3 py-2 text-[#333] whitespace-nowrap">{date || "—"}</td>
                                    <td className="px-3 py-2">
                                        <div className="text-[13px] text-charcoal">{r.title || "(untitled)"}</div>
                                        {r.overdue && <div className="text-[11px] uppercase text-terracotta">Overdue</div>}
                                    </td>
                                    <td className="px-3 py-2">
                                        <span className={`inline-block text-[11px] uppercase tracking-wide px-2 py-0.5 rounded-full border ${tone}`}
                                              data-testid={`rfi-list-status-${r.id}`}>
                                            {r.status}
                                        </span>
                                    </td>
                                    <td className="px-3 py-2 text-[#666]">{Array.isArray(r.items) ? r.items.length : "—"}</td>
                                    <td className="px-3 py-2 text-[#666] whitespace-nowrap">{due}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {showNew && (
                <UnifiedRFIModal
                    title="New Information Request"
                    onClose={closeNew}>
                    <RFIAuthoring
                        key={`new-${newKey}`}
                        projectId={id}
                        onClose={closeNew}
                        onSaved={() => { load(); }}
                        onIssued={() => { closeNew(); load(); }} />
                </UnifiedRFIModal>
            )}

            {openRid && openMode === "draft" && (
                <UnifiedRFIModal
                    title="Edit Draft Information Request"
                    onClose={closeRow}
                    footer={
                        <DeleteRFILink
                            rid={openRid}
                            mode="draft"
                            onDeleted={() => { closeRow(); load(); }} />
                    }>
                    <RFIAuthoring
                        key={`draft-${openRid}`}
                        projectId={id}
                        initialDraft={openInitial}
                        onClose={closeRow}
                        onSaved={() => { load(); }}
                        onIssued={() => { closeRow(); load(); }} />
                </UnifiedRFIModal>
            )}

            {openRid && openMode === "issued" && (
                <UnifiedRFIModal
                    title="Information Request Review"
                    onClose={closeRow}
                    footer={
                        <DeleteRFILink
                            rid={openRid}
                            mode="issued"
                            row={openRow_}
                            onDeleted={() => { closeRow(); load(); }} />
                    }>
                    <RFIReviewPanel
                        key={`issued-${openRid}`}
                        projectId={id}
                        rid={openRid}
                        onRefresh={(r) => { setOpenRow_(r); load(); }} />
                </UnifiedRFIModal>
            )}
        </div>
    );
}

/* Unified RFI modal shell — same size and structure as the previous
   New-RFI modal (largest existing modal frame in the codebase).
   Provides a header slot (title + close X), a scrolling body, and an
   optional footer for the Delete link. */
function UnifiedRFIModal({ title, children, footer, onClose }) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
             data-testid="rfi-unified-modal">
            <div className="bg-white rounded-lg shadow-lg w-[640px] max-w-[92vw] max-h-[92vh] flex flex-col border border-[#E5E2DC]">
                <div className="flex items-center justify-between px-5 py-3 border-b border-[#E5E2DC]">
                    <div className="font-manrope font-semibold text-[15px]" data-testid="rfi-unified-modal-title">{title}</div>
                    <button onClick={onClose}
                        aria-label="Close"
                        data-testid="rfi-unified-modal-close"
                        className="text-[#666] hover:text-charcoal p-1 rounded-md">
                        <X className="w-4 h-4" />
                    </button>
                </div>
                <div className="p-5 overflow-y-auto flex-1">
                    {children}
                </div>
                {footer && (
                    <div className="flex justify-end px-5 py-2 border-t border-[#E5E2DC] bg-[#FBFAF6]" data-testid="rfi-unified-modal-footer">
                        {footer}
                    </div>
                )}
            </div>
        </div>
    );
}

/* Delete link + nested confirmation dialog.  Draft: always enabled.
   Issued: enabled only when no item has a client-side response
   recorded (client_state === "Outstanding" for every item).  On
   destructive confirm, DELETE /admin/information-requests/{rid}. */
function DeleteRFILink({ rid, mode, row, onDeleted }) {
    const [confirming, setConfirming] = useState(false);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const CLIENT_OUTSTANDING = "Outstanding";
    const enabled = mode === "draft"
        || (row && Array.isArray(row.items) && row.items.every(
            it => (it.client_state || CLIENT_OUTSTANDING) === CLIENT_OUTSTANDING));
    const tooltip = enabled ? "" : "Cannot delete — client has responded to one or more items.";
    const doDelete = async () => {
        setBusy(true); setErr("");
        try {
            await api.delete(`/admin/information-requests/${rid}`);
            toast.success("RFI deleted");
            setConfirming(false);
            if (onDeleted) onDeleted();
        } catch (e) {
            setErr(readErrorDetail(e, "Delete failed"));
        } finally { setBusy(false); }
    };
    return (
        <>
            <button
                onClick={() => enabled && setConfirming(true)}
                disabled={!enabled}
                title={tooltip}
                data-testid="rfi-delete-link"
                className={`text-[12px] underline ${enabled ? "text-terracotta hover:text-[#8A3F2F]" : "text-[#B8B4AB] cursor-not-allowed"}`}>
                Delete RFI
            </button>
            {confirming && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50"
                     data-testid="rfi-delete-confirm-modal">
                    <div className="bg-white rounded-lg shadow-lg w-[420px] max-w-[92vw] p-4 flex flex-col gap-3 border border-[#E5E2DC]">
                        <div className="font-manrope font-semibold text-[14px]">Delete this RFI?</div>
                        <div className="text-[13px] text-[#333]">
                            This will permanently remove the RFI and all its items. This cannot be undone.
                        </div>
                        {err && <div className="text-[12px] text-terracotta" data-testid="rfi-delete-error">{err}</div>}
                        <div className="flex justify-end gap-2">
                            <button onClick={() => setConfirming(false)} disabled={busy}
                                data-testid="rfi-delete-cancel"
                                className="text-[13px] px-3 py-1.5 border border-[#E5E2DC] rounded-md">
                                Cancel
                            </button>
                            <button onClick={doDelete} disabled={busy}
                                data-testid="rfi-delete-confirm"
                                className="text-[13px] px-3 py-1.5 bg-terracotta text-white rounded-md disabled:opacity-40">
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

